import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { cutoffDate, type DateRangeKey } from '../../components/listDefaults';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';
import { STAGES, stageStatus, type StageStatus } from './jobProgressStages';
import './backlog.css';

/* ─── FB11 — Job Progress Tracking ──────────────────────────────────────────
 * Matches the alpaca_intelligence_v5 HTML mockup verbatim:
 *   • 4 KPI cards (Total / Submitted / Blended Run / Avg Progress)
 *   • 8-segment pipeline progress bar, each segment = 12.5%
 *   • Hover tooltip shows stage name + "n/8 · Complete|Partial|Pending"
 *   • Clickable Job ID (deep-link placeholder)
 * Progress is computed server-side; per-stage status is derived from that % by
 * `stageStatus` in ./jobProgressStages (a stage reads Complete only once the
 * work has moved PAST it — see that file for why the boundary is exclusive).
 * The grid itself (sort / per-column filter / pagination / footer) is the shared
 * <AppDataGrid>; the progress bar lives in a custom column `render`.
 * ──────────────────────────────────────────────────────────────────────────── */

interface JobProgressRow {
  jobOid:           string;
  jobId:            string;
  receivedDate:     string | null;
  dueDate:          string | null;
  client:           string | null;
  projectId:        string | null;
  projectName:      string | null;
  jobStatus:        number | null;
  totalTests:       number;
  pendingCount:     number;
  inReviewCount:    number;
  reportingCount:   number;
  reportedCount:    number;
  progressPct:      number;
  // Real Subout / Prep stage counts. OPTIONAL because a server whose snapshot
  // predates the 2026-08-07 columns simply omits them — those segments then read
  // "not tracked" instead of wrongly claiming the job has no subout/prep work.
  suboutTotal?:     number;
  suboutDone?:      number;
  prepRequired?:    number;
  prepDone?:        number;
}

// Dates render via the shared §14b.6 helpers (MM/DD/YYYY [HH:mm], 24-hour,
// never inline date code); '—' placeholder for empty values, raw fallback
// when unparseable.  Date-only values stay date-only (no fake 00:00).
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return fmtLocalDate(iso) || iso;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  if (!/T\d{2}:\d{2}/.test(iso)) return fmtDate(iso);
  return fmtLocalDateTime(iso) || iso;
}


/* token-exempt: chart — pipeline-stage segment status colours (data-viz) */
const STAGE_COLORS: Record<StageStatus, string> = {
  Complete: '#27ae60',
  Partial:  '#f39c12',
  Pending:  '#e5e7eb',
  Blocked:  '#e74c3c',
  // The snapshot holds no data for this stage. A hatch reads as "not measured";
  // a flat fill cannot — every solid colour here asserts something about
  // progress, and that assertion is exactly what was wrong (2026-08-07).
  NotTracked: 'repeating-linear-gradient(45deg,#f1f2f4 0 3px,#e2e5ea 3px 6px)',
};

/** Tooltip labels — never surface the raw union member to a user. */
const STAGE_STATUS_LABEL: Record<StageStatus, string> = {
  Complete:   'Complete',
  Partial:    'Partial',
  Pending:    'Pending',
  Blocked:    'Blocked',
  NotTracked: 'Not tracked',
};

export default function JobProgressPage() {
  const [rows,    setRows]    = useState<JobProgressRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState<string | null>(null);
  const [range,   setRange]   = useState<DateRangeKey>('1M');
  const [search,  setSearch]  = useState<string>('');
  const [loadMs,  setLoadMs]  = useState<number | null>(null);
  /** Tooltip state for the hover-over-stage interaction. */
  const [tip, setTip] = useState<{ x: number; y: number; stage: string; idx: number; status: StageStatus } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const t0 = performance.now();
    try {
      const co = cutoffDate(range);
      const sinceDate = co
        ? `${co.getFullYear()}-${String(co.getMonth() + 1).padStart(2, '0')}-${String(co.getDate()).padStart(2, '0')}`
        : '';
      const url = sinceDate
        ? `/api/v1/backlog/job-progress?sinceDate=${sinceDate}`
        : '/api/v1/backlog/job-progress';
      const res = await api.get<{ success: boolean; data: JobProgressRow[] }>(url);
      setRows(res.data?.data ?? []);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setErr(msg ?? (e as Error).message ?? 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { void load(); }, [load]);

  // KPI math — matches the mockup labels exactly.
  const kpis = useMemo(() => {
    const total = rows.length;
    const submitted = rows.filter(r => r.progressPct > 0).length;
    const submittedPct = total === 0 ? 0 : Math.round((submitted / total) * 100);
    // "Blended Run" = jobs whose project name contains "Blended" (Houston PW
    // categorises blended-water samples that way — used in the dashboard KPI).
    const blended = rows.filter(r => (r.projectName ?? '').toLowerCase().includes('blended')).length;
    // "today" = jobs received in the last 24h.
    const today = (() => {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      return rows.filter(r => r.receivedDate && new Date(r.receivedDate).getTime() >= cutoff).length;
    })();
    const avg = total === 0 ? 0
      : Math.round(rows.reduce((s, r) => s + Number(r.progressPct), 0) / total);
    return { total, submitted, submittedPct, blended, today, avg };
  }, [rows]);

  // Page-level search filter only — AppDataGrid owns per-column filter, sort and pagination.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      (r.jobId ?? '').toLowerCase().includes(q) ||
      (r.client ?? '').toLowerCase().includes(q) ||
      (r.projectName ?? '').toLowerCase().includes(q) ||
      (r.projectId ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  // Shared-grid columns.  Job ID is a link, Progress is the 8-segment bar (both
  // via `render`); the rest are plain black text.  `text` keeps each custom
  // column filterable / sortable on a sensible value.
  const gridCols: AppColumn<JobProgressRow>[] = [
    { field: 'jobId', header: 'Job ID', width: 110,
      render: r => <a className="jpt-link" href="#" onClick={e => e.preventDefault()}>{r.jobId}</a>,
      text: r => r.jobId ?? '' },
    { field: 'client', header: 'Client', width: 170, text: r => r.client ?? '' },
    { field: 'projectId', header: 'Project ID', width: 130, text: r => r.projectId ?? '—' },
    { field: 'projectName', header: 'Project Name',
      render: r => (
        <span title={r.projectName ?? ''} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
          {r.projectName ?? '—'}
        </span>
      ),
      text: r => r.projectName ?? '—' },
    { field: 'receivedDate', header: 'Received Date', width: 150,
      render: r => fmtDateTime(r.receivedDate), text: r => fmtDateTime(r.receivedDate) },
    { field: 'dueDate', header: 'Due Date', width: 110,
      render: r => fmtDate(r.dueDate), text: r => fmtDate(r.dueDate) },
    { field: 'progressPct', header: 'Progress (hover for stage)', minWidth: 260,
      text: r => `${Math.round(Number(r.progressPct ?? 0))}%`,
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', flex: 1, gap: 1, height: 14 }}>
            {STAGES.map((stage, sIdx) => {
              // Pass the real counts: a stage goes green only when its work is
              // finished for every sample, never because the aggregate % says so.
              const status = stageStatus(Number(r.progressPct), sIdx, {
                totalTests:     Number(r.totalTests ?? 0),
                pendingCount:   Number(r.pendingCount ?? 0),
                inReviewCount:  Number(r.inReviewCount ?? 0),
                reportingCount: Number(r.reportingCount ?? 0),
                reportedCount:  Number(r.reportedCount ?? 0),
                // Left undefined when the server did not send them — do NOT
                // coalesce to 0, which would read as "no subout/prep work".
                suboutTotal:    r.suboutTotal   == null ? undefined : Number(r.suboutTotal),
                suboutDone:     r.suboutDone    == null ? undefined : Number(r.suboutDone),
                prepRequired:   r.prepRequired  == null ? undefined : Number(r.prepRequired),
                prepDone:       r.prepDone      == null ? undefined : Number(r.prepDone),
              });
              const color = STAGE_COLORS[status];
              return (
                <div
                  key={sIdx}
                  style={{
                    flex: 1,
                    background: color,
                    border: status === 'Pending' || status === 'NotTracked'
                      ? '1px solid var(--ai-line)' : 'none',
                    borderRadius: sIdx === 0 ? '3px 0 0 3px' : sIdx === 7 ? '0 3px 3px 0' : 0,
                    cursor: 'help',
                    transition: 'opacity .15s',
                  }}
                  onMouseEnter={e => {
                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    setTip({
                      x: rect.left + rect.width / 2,
                      y: rect.top - 8,
                      stage,
                      idx: sIdx + 1,
                      status,
                    });
                  }}
                  onMouseLeave={() => setTip(null)}
                />
              );
            })}
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, color: 'var(--ai-ink)',
            minWidth: 38, textAlign: 'right',
          }}>{Math.round(Number(r.progressPct))}%</span>
        </div>
      ),
    },
  ];

  // Full-format "Export to" (all 10 ALPACA formats) of the currently-filtered
  // rows, built from the SAME grid columns/display text so the file matches the
  // grid exactly — via the shared <ExportMenu> (AppToolbar exportData).
  const exportData: ExportData = {
    filename: 'job-progress',
    ...gridExportMatrix(filtered, gridCols),
  };

  return (
    <div className="bl-page" style={{ position: 'relative' }}>
      {/* ── Fixed top: breadcrumb + title + KPI cards (no scroll) ─────── */}
      <div className="bl-top">
        <div style={{ padding: '14px 0 6px' }}>
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 4 }}>
            Home &nbsp;›&nbsp; Task Tracker &nbsp;›&nbsp;
            {/* token-exempt: chart — Task Tracker navy accent */}
            <span style={{ color: '#1F4E79', fontWeight: 600 }}>Job Progress Tracking</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta('jobprog') + the TOP load-time chip. */}
          <FormTitle pageId="jobprog" loadMs={loadMs} loading={loading} />
          <div className="mlv-subcap" style={{ fontSize: 11, color: 'var(--ai-muted)', marginTop: 3 }}>
            Read-only · {kpis.total} incomplete jobs · Date Filter: {range}
          </div>
        </div>

        {/* KPI accent colours mirror the pipeline legend (data-viz) — token-exempt: chart. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, padding: '4px 0 8px' }}>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Total Jobs</div>
            <div className="jpt-kpi-val" style={{ color: '#1F4E79' }}>{kpis.total}</div>
            <div className="jpt-kpi-sub">incomplete</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Submitted</div>
            <div className="jpt-kpi-val" style={{ color: '#27ae60' }}>{kpis.submitted}</div>
            <div className="jpt-kpi-sub" style={{ color: '#27ae60' }}>{kpis.submittedPct}%</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Blended Run</div>
            <div className="jpt-kpi-val" style={{ color: 'var(--ai-ink)' }}>{kpis.blended}</div>
            <div className="jpt-kpi-sub">{kpis.today} today</div>
          </div>
          <div className="jpt-kpi">
            <div className="jpt-kpi-lbl">Avg Progress</div>
            <div className="jpt-kpi-val" style={{ color: 'var(--ai-ink)' }}>{kpis.avg}%</div>
            <div className="jpt-kpi-sub">latest snapshot</div>
          </div>
        </div>
      </div>

      {/* ── Grid area: toolbar + search sit ON the grey page (BO UI Standard v2,
          like ModuleListView); only the grid card below is white. ─────────── */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — shared <AppToolbar> (§18.6):
            left = form-specific Date Filter · Refresh; Export pushed hard right
            via the shared spacer.  Read-only dashboard (no CRUD). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <DateRangeSelect variant="dropdown" label="Date Filter" ranges={TRACKING_RANGES}
                value={TRACKING_RANGES.find(r => r.label === range)?.days ?? null}
                onChange={(d) => setRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            }
            onRefresh={load}
            refreshing={loading}
            exportData={exportData}
          />
        </div>

        {/* Search — shared .list-search-row on the grey page (boStandard.css),
            NOT inside the card and NOT full-width. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              placeholder="Search job ID, client, project name…"
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <span className="list-search-clear" onClick={() => setSearch('')}>×</span>
            )}
          </div>
        </div>

        <div className="bl-card bl-card-flex">
        {/* Legend */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '6px 14px',
          fontSize: 11, color: 'var(--ai-muted)', borderBottom: '1px solid var(--ai-line-soft)', background: 'var(--ai-header-bg)',
        }}>
          {/* token-exempt: chart — Task Tracker navy accent */}
          <span style={{ fontWeight: 700, color: '#1F4E79' }}>8-stage pipeline:</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: STAGE_COLORS.Complete, borderRadius: 2 }} />
            Complete
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: STAGE_COLORS.Partial, borderRadius: 2 }} />
            Partial
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: STAGE_COLORS.Pending, borderRadius: 2, border: '1px solid var(--ai-line)' }} />
            Pending
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: STAGE_COLORS.Blocked, borderRadius: 2 }} />
            Blocked
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, background: STAGE_COLORS.NotTracked, borderRadius: 2, border: '1px solid var(--ai-line)' }} />
            Not tracked
          </span>
          <span style={{ marginLeft: 'auto', color: 'var(--ai-muted)', fontSize: 10 }}>
            Hover segment for stage name · hatched stages are not captured yet
          </span>
        </div>

        {err && (
          <div style={{ padding: '10px 14px', background: 'var(--ai-pill-alert-bg)', color: 'var(--ai-red)', fontSize: 12 }}>
            <i className="ti ti-alert-triangle" /> {err}
          </div>
        )}

        {/* Shared data grid — styling, per-column filters, sort, footer /
            pagination and load-time are all standardised in <AppDataGrid>. */}
        <AppDataGrid<JobProgressRow>
          rows={filtered}
          columns={gridCols}
          getRowId={r => r.jobOid}
          loading={loading}
          loadMs={loadMs}
          emptyText="No incomplete jobs in this window."
        />
        </div>{/* /.bl-card.bl-card-flex */}
      </div>{/* /.bl-grid-area */}

      {/* Hover tooltip — fixed positioning, follows the stage segment */}
      {tip && (
        /* token-exempt: chart — dark hover tooltip surface + data-viz status dot */
        <div
          style={{
            position: 'fixed',
            left: tip.x,
            top:  tip.y,
            transform: 'translate(-50%, -100%)',
            background: '#1f2937',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: 11,
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ fontWeight: 700 }}>{tip.stage}</div>
          <div style={{ marginTop: 2, opacity: 0.85 }}>
            <span style={{
              display: 'inline-block', width: 8, height: 8,
              background: STAGE_COLORS[tip.status], borderRadius: '50%',
              marginRight: 5, verticalAlign: 'middle',
            }} />
            {tip.idx}/8 · {STAGE_STATUS_LABEL[tip.status]}
          </div>
          {tip.status === 'NotTracked' && (
            <div style={{ marginTop: 3, opacity: 0.7, fontSize: 10, maxWidth: 190 }}>
              No data is captured for this stage, so its progress is unknown.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
